import { elMessage } from '@/utils';
import { useI18n } from 'vue-i18n';

export interface ExcelImportResponse {
  code?: string;
  msg?: string;
}

export interface UseExcelImportOptions {
  request?: (formData: FormData) => Promise<any>;
  setLoading?: (loading: boolean) => void;
  onSuccess?: (response: ExcelImportResponse) => void | Promise<void>;
  onFileSelected?: (file: File) => void | Promise<void>;
  fileExtensions?: string[];
  invalidFileMessage?: string;
  successMessage?: string;
  fileField?: string;
}

/**
 * 提供 Excel 文件选择、校验和上传的通用能力。
 *
 * 使用示例：
 * ```ts
 * import { useExcelImport } from '@/utils/useExcelImport';
 * import { importTemplateApi } from '@/api/mediaManagement';
 *
 * const { openExcelImportDialog } = useExcelImport({
 *   // 接收 FormData 的导入接口；默认以 file 字段传递所选文件。
 *   request: importTemplateApi,
 *   // 将导入过程绑定到页面自身的 loading 状态。
 *   setLoading: (loading) => {
 *     tableConfig.options.loading = loading;
 *   },
 *   // 接口返回 code === '0' 后执行，通常用于刷新列表。
 *   onSuccess: async () => {
 *     await getData();
 *   },
 * });
 *
 * // 仅选择并获取文件，暂不调用上传接口：
 * const { openExcelImportDialog: selectExcelFile } = useExcelImport({
 *   onFileSelected: (file) => {
 *     selectedFile.value = file;
 *   },
 * });
 *
 * // 配置 CommonTable 表头按钮：
 * // { label: '导入', func: openExcelImportDialog }
 * ```
 *
 * 可选配置：request、setLoading、onSuccess、onFileSelected、fileExtensions
 * （默认 ['xlsx', 'xls']）、successMessage、fileField（默认 'file'）。
 * 未提供 request 时仅选择并校验文件，不会发起请求或显示成功提示。
 * 业务失败和网络异常提示继续由请求实例统一处理。
 */
export const useExcelImport = (options: UseExcelImportOptions) => {
  const { t } = useI18n();
  const fileExtensions = (options.fileExtensions || ['xlsx', 'xls'])
    .map((extension) => extension.replace(/^\./, '').toLowerCase());

  const openExcelImportDialog = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = fileExtensions.map((extension) => `.${extension}`).join(',');

    input.onchange = async (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!extension || !fileExtensions.includes(extension)) {
        elMessage('warning', t(options.invalidFileMessage || '仅支持上传 Excel 文件'));
        return;
      }

      try {
        await options.onFileSelected?.(file);
        // 未提供接口时仅选择文件，适用于接口尚未接入的场景。
        if (!options.request) return;

        options.setLoading?.(true);
        const formData = new FormData();
        formData.append(options.fileField || 'file', file);
        const response = await options.request(formData) as ExcelImportResponse;
        if (response.code == '0') {
          elMessage('success', t(options.successMessage || response.msg || '导入成功'));
          await options.onSuccess?.(response);
        }
      } catch (error) {
        // 请求实例会统一处理网络和业务异常提示。
      } finally {
        options.setLoading?.(false);
      }
    };

    input.click();
  };

  return { openExcelImportDialog };
};
